import { Check, MessageCircle, ShieldCheck } from "lucide-react";
import { PRODUCT_CATEGORIES, CONTACT, SEO_PAGES } from "@/data/site";
import { PageHero, ConversionRow } from "@/components/Shared";
import { Seo } from "@/components/Seo";
import { useCertificate } from "@/components/CertificateProvider";

const Products = () => {
  const { open } = useCertificate();
  return (
    <div data-testid="products-page">
      <Seo title={SEO_PAGES.products.title} description={SEO_PAGES.products.description} />
      <PageHero
        eyebrow="Our Products"
        title="Natural goodness, in every form"
        intro="From cold-pressed juices to heritage drinks, freshly baked pastries and aromatic spices — discover the full Faf's range."
        image={PRODUCT_CATEGORIES[0].image}
        imageAlt="Faf's product range — fresh juices, traditional drinks, snacks and spices"
      />

      <section className="pb-8">
        <div className="container-px mx-auto max-w-[1400px] space-y-20 lg:space-y-28">
          {PRODUCT_CATEGORIES.map((p, i) => {
            const reversed = i % 2 === 1;
            return (
              <div
                key={p.id}
                data-testid={`product-section-${p.id}`}
                className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-center"
              >
                <div className={`img-zoom-wrap relative overflow-hidden rounded-[2rem] aspect-[4/3] ${reversed ? "lg:order-2" : ""}`}>
                  <img src={p.image} alt={p.name} className="img-zoom h-full w-full object-cover" />
                </div>
                <div className={reversed ? "lg:order-1" : ""}>
                  <span className="eyebrow text-brand-purple">{p.tagline}</span>
                  <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-brand-ink">{p.name}</h2>
                  <p className="mt-4 text-brand-slate leading-relaxed text-base md:text-lg">{p.description}</p>
                  <ul className="mt-6 grid grid-cols-2 gap-3">
                    {p.items.map((item) => (
                      <li key={item} className="flex items-center gap-2.5 text-brand-ink">
                        <span className="grid h-6 w-6 place-items-center rounded-full bg-brand-mist text-brand-greenDark shrink-0"><Check size={13} /></span>
                        <span className="text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href={CONTACT.whatsappOrderHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`product-order-${p.id}`}
                    className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand-green px-7 py-3.5 text-sm font-medium text-white transition-all duration-300 hover:bg-brand-greenDark hover:-translate-y-0.5"
                  >
                    <MessageCircle size={17} /> Order {p.name}
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section data-testid="products-trust" className="pt-4">
        <div className="container-px mx-auto max-w-[1400px]">
          <p className="flex items-center justify-center gap-2.5 text-center text-sm text-brand-slate">
            <ShieldCheck size={17} className="text-brand-green shrink-0" />
            <span>
              All products are{" "}
              <button
                type="button"
                data-testid="products-trust-fda"
                onClick={() => open(0)}
                className="font-medium text-brand-green underline underline-offset-4 decoration-brand-green/30 transition-colors hover:decoration-brand-green"
              >
                FDA registered
              </button>{" "}
              and produced under{" "}
              <button
                type="button"
                data-testid="products-trust-safety"
                onClick={() => open(2)}
                className="font-medium text-brand-green underline underline-offset-4 decoration-brand-green/30 transition-colors hover:decoration-brand-green"
              >
                approved food safety standards
              </button>
              .
            </span>
          </p>
        </div>
      </section>

      <section className="section-py">
        <div className="container-px mx-auto max-w-[1400px]">
          <div className="rounded-[2.5rem] bg-brand-alt border border-black/5 p-10 lg:p-16 text-center">
            <h2 className="mx-auto max-w-2xl text-3xl sm:text-4xl font-semibold tracking-tight text-brand-ink">
              Looking for something custom or in bulk?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-brand-slate">
              We supply hotels, offices, schools and events. Tell us what you
              need and we'll craft the perfect order.
            </p>
            <div className="mt-8 flex justify-center"><ConversionRow testidPrefix="products-cta" /></div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
